import { useEffect, useState } from "react";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "../../../components/ui/select";
import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import { Checkbox } from "../../../components/ui/checkbox";
import { PencilIcon, Eye, EyeOff } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import toast from "react-hot-toast";
import server from "../../../utils/serverConfig";

const statusOptions = [
  { value: "active", label: "active" },
  { value: "inactive", label: "inactive" },
  { value: "deactivated", label: "deactivated" },
  { value: "dummy", label: "dummy" },
];

export function EditNubraUserModal({ userDetails, fetchFyersUserList, forceOpen = false, onForceClose }) {
  const [open, setOpen] = useState(false);
  const [showCustomLabelInput, setShowCustomLabelInput] = useState(false);

  // Single state object for all input fields
  const [userData, setUserData] = useState({
    clientId: "",
    name: "",
    email: "",
    totpKey: "",
    pin: "",
    phoneNumber: "",
    status: "",
    label: "",
    proxyUser: "",
    proxyPassword: "",
    proxyHost: "",
    proxyPort: 0,
    proxyToggle: false,
    proxyStaticIp: "",
  });

  const [visibleFields, setVisibleFields] = useState({});

  const toggleVisibility = (field) => {
    setVisibleFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  // Fetch user details on component load
  const getDecryptedUserDetails = async () => {
    const data = {
      client_id: userDetails.client_id,
    };

    const config = {
      method: "post",
      url: `${server.godseye.baseUrl}get-nubra-user-by-id`,
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify(data),
    };

    try {
      const response = await apiClient.request(config);

      // Populate the form with the fetched data
      setUserData({
        clientId: response.data.client_id || "",
        name: response.data.name || "",
        email: response.data.email || "",
        totpKey: response.data.totp_key || "",
        pin: response.data.pin || "",
        phoneNumber: response.data.phone || "",
        status: response.data.status || "",
        label: response.data.label || "",
        proxyUser: response.data.proxy_user || "",
        proxyPassword: response.data.proxy_password || "",
        proxyHost: response.data.proxy_host || "",
        proxyPort: response.data.proxy_port || 0,
        proxyToggle: response.data.proxy_toggle || false,
        proxyStaticIp: response.data.proxy_static_ip || "",
      });

      // Check if we need to show custom input
      const predefinedLabels = ["clearmind", "satyam"];
      setShowCustomLabelInput(!predefinedLabels.includes(response.data.label || "") && response.data.label !== "");
    } catch (error) {
      console.error("Failed to get user details:", error);
    }
  };

  // Common handleChange function
  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateUser = () => {
    const data = {
      client_id: userData.clientId,
      name: userData.name,
      email: userData.email,
      totp_key: userData.totpKey,
      pin: userData.pin,
      status: userData.status,
      phone_number: userData.phoneNumber,
      label: userData.label,
      proxy_user: userData.proxyUser,
      proxy_password: userData.proxyPassword,
      proxy_host: userData.proxyHost,
      proxy_port: parseInt(userData.proxyPort),
      proxy_toggle: userData.proxyToggle,
      proxy_static_ip: userData.proxyStaticIp || null,
    };

    console.log("Updated User Data", data);

    let config = {
      method: "post",
      url: `${server.godseye.baseUrl}edit-nubra-user`,
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify(data),
    };

    apiClient
      .request(config)
      .then((response) => {
        console.log("Server response:", response.data);
        toast.success("Nubra user updated successfully!", {
          duration: 5000,
        });
      })
      .catch((error) => {
        console.error("Failed to update user", error);
        toast.error("Failed to update user", {
          duration: 5000,
        });
      })
      .finally(() => {
        setOpen(false);
        fetchFyersUserList();
      });
  };

  const handleOpenModal = () => {
    setOpen(true);
    getDecryptedUserDetails();
  };

  useEffect(() => {
    if (forceOpen) {
      handleOpenModal();
    }
  }, [forceOpen]);

  return (
    <Dialog open={open} onOpenChange={(val) => { setOpen(val); if (!val && onForceClose) onForceClose(); }}>
      <DialogTrigger asChild>
        <Button
          onClick={handleOpenModal}
          variant="outline"
          className="text-[12px] h-[25px] font-medium bg-zinc-800 text-white hover:bg-zinc-700"
        >
          <PencilIcon className="mr-2 w-3 h-3 text-gray-500" />
          Edit User
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[1025px] border-zinc-700">
        <DialogHeader>
          <DialogTitle className="text-white">
            Edit Nubra User Details
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-4 py-4">
          {Object.keys(userData)
            .filter((field) => !["status", "label", "proxyToggle", "proxyStaticIp"].includes(field))
            .map((field) => (
              <div key={field} className="grid grid-cols-1 items-center gap-4">
                <Label className="text-white">
                  {field.replace(/([A-Z])/g, " $1")}
                </Label>
                <div className="relative">
                  <Input
                    className="text-white pr-10"
                    type={
                      (field.toLowerCase().includes("password") || 
                       field.toLowerCase().includes("secret") || 
                       field.toLowerCase().includes("key") ||
                       field.toLowerCase().includes("pin") ||
                       field.toLowerCase().includes("token")) && !visibleFields[field] 
                        ? "password" 
                        : "text"
                    }
                    placeholder={field.replace(/([A-Z])/g, " $1")}
                    name={field}
                    value={userData[field]}
                    onChange={handleChange}
                  />
                  {(field.toLowerCase().includes("password") || 
                    field.toLowerCase().includes("secret") || 
                    field.toLowerCase().includes("key") ||
                    field.toLowerCase().includes("pin") ||
                    field.toLowerCase().includes("token")) && (
                    <button
                      type="button"
                      onClick={() => toggleVisibility(field)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                    >
                      {visibleFields[field] ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}

          {/* Custom Status Dropdown */}
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Status</Label>
            <Select
              value={userData.status}
              onValueChange={(val) =>
                setUserData((prev) => ({ ...prev, status: val }))
              }
            >
              <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
                <SelectValue placeholder="Select Status" />
              </SelectTrigger>
              <SelectContent className="border-zinc-700">
                <SelectGroup>
                  {statusOptions.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {/* Label Dropdown + Custom Input */}
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Label</Label>
            <Select
              value={showCustomLabelInput ? "custom" : userData.label}
              onValueChange={(val) => {
                if (val === "custom") {
                  setShowCustomLabelInput(true);
                  // Keep existing custom value or clear for new input
                } else {
                  setShowCustomLabelInput(false);
                  setUserData((prev) => ({ ...prev, label: val }));
                }
              }}
            >
              <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
                <SelectValue placeholder="Select Label" />
              </SelectTrigger>
              <SelectContent className="border-zinc-700">
                <SelectGroup>
                  <SelectItem value="clearmind">clearmind</SelectItem>
                  <SelectItem value="satyam">satyam</SelectItem>
                  <SelectItem value="custom">Custom (Type your own)</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
            
          </div>


          
          {/* Label Dropdown + Custom Input */}
            {showCustomLabelInput && ( 
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Label</Label>
           
              <Input
                className="text-white mt-2"
                type="text"
                placeholder="Enter custom label"
                name="label"
                value={userData.label}
                onChange={handleChange}
              />
          </div>
            )}
          {/* Proxy Static IP */}
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Proxy Static Ip</Label>
            <Input
              className="text-white"
              type="text"
              placeholder="e.g. 2402:1f00:8300:989::1"
              name="proxyStaticIp"
              value={userData.proxyStaticIp}
              onChange={handleChange}
            />
          </div>

          <div className="flex items-center space-x-2 h-full pt-8">
            <Checkbox
              id="proxy-toggle"
              checked={userData.proxyToggle}
              onCheckedChange={(checked) =>
                setUserData((prev) => ({ ...prev, proxyToggle: checked }))
              }
            />
            <Label htmlFor="proxy-toggle" className="text-white cursor-pointer">
              Proxy Toggle
            </Label>
          </div>
        </div>

        <DialogFooter className="mt-6 flex justify-between">
          <Button
            type="submit"
            className="text-[16px] font-normal bg-zinc-800 text-white hover:bg-zinc-700"
            onClick={handleUpdateUser}
          >
            Update User Details
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}












































































// import { useEffect, useState } from "react";
// import { Button } from "../../../components/ui/button";
// import {
//   Dialog,
//   DialogContent,
//   DialogHeader,
//   DialogTitle,
//   DialogFooter,
//   DialogTrigger,
// } from "../../../components/ui/dialog";
// import {
//   Select,
//   SelectTrigger,
//   SelectValue,
//   SelectContent,
//   SelectGroup,
//   SelectItem,
// } from "../../../components/ui/select";
// import { Label } from "../../../components/ui/label";
// import { Input } from "../../../components/ui/input";
// import { PencilIcon } from "lucide-react";
// import apiClient from "../../../utils/apiClient";
// import toast from "react-hot-toast";

// const statusOptions = [
//   { value: "active", label: "active" },
//   { value: "inactive", label: "inactive" },
//   { value: "deactivated", label: "deactivated" },
//   { value: "dummy", label: "dummy" },
// ];

// export function EditNubraUserModal({ userDetails, fetchFyersUserList }) {
//   const [open, setOpen] = useState(false);

//   // Single state object for all input fields
//   const [userData, setUserData] = useState({
//     clientId: "",
//     name: "",
//     email: "",
//     totpKey: "",
//     pin: "",
//     phoneNumber: "",
//     status: "",
//   });

//   // Fetch user details on component load
//   const getDecryptedUserDetails = async () => {
//     const data = {
//       client_id: userDetails.client_id,
//     };

//     const config = {
//       method: "post",
//       url: "https://godseye.server.sypto.xyz/get-nubra-user-by-id",
//       // url: "https://godseye.server.sypto.xyz/get-nubra-user-by-id",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       data: JSON.stringify(data), // Convert the data object to a JSON string
//     };

//     try {
//       const response = await apiClient.request(config);

//       // Populate the form with the fetched data
//       setUserData({
//         clientId: response.data.client_id || "",
//         name: response.data.name || "",
//         email: response.data.email || "",
//         totpKey: response.data.totp_key || "",
//         pin: response.data.pin || "",
//         phoneNumber: response.data.phone || "",
//         status: response.data.status || "",
//       });
//     } catch (error) {
//       console.error("Failed to get user details:", error);
//     }
//   };

//   // Common handleChange function
//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setUserData((prev) => ({ ...prev, [name]: value }));
//   };

//   const handleUpdateUser = () => {
//     const data = {
//       client_id: userData.clientId,
//       name: userData.name,
//       email: userData.email,
//       totp_key: userData.totpKey,
//       pin: userData.pin,
//       status: userData.status,
//       phone_number: userData.phoneNumber,
//     };

//     console.log("Updated User Data", data);

//     let config = {
//       method: "post",
//       url: "https://godseye.server.sypto.xyz/edit-nubra-user",
//       // url: "https://godseye.server.sypto.xyz/edit-fyers-user",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       data: JSON.stringify(data), // Convert the data object to a JSON string
//     };

//     axios
//       .request(config)
//       .then((response) => {
//         console.log("Server response:", response.data);
//         // Optionally handle the response data here
//       })
//       .catch((error) => {
//         console.error("Failed to update user", error);
//         fetchFyersUserList();
//       })
//       .finally(() => {
//         setOpen(false); // Close the modal or perform any final actions
//         fetchFyersUserList();
//       });

//     // API request logic goes here
//     setOpen(false); // Close the modal after update
//   };

//   const handleOpenModal = () => {
//     setOpen(true);
//     getDecryptedUserDetails(userDetails.client_id, setUserData);
//   };

 

//   return (
//     <Dialog open={open} onOpenChange={setOpen}>
//       <DialogTrigger asChild>
//         <Button
//           onClick={handleOpenModal}
//           variant="outline"
//           className="text-[12px] h-[25px] font-medium bg-zinc-800 text-white hover:bg-zinc-700"
//         >
//           <PencilIcon className="mr-2 w-3 h-3 text-gray-500" />
//           Edit User
//         </Button>
//       </DialogTrigger>
//       <DialogContent className="sm:max-w-[1025px] border-zinc-700">
//         <DialogHeader>
//           <DialogTitle className="text-white">
//             Edit (User Name) Details
//           </DialogTitle>
//         </DialogHeader>
//         <div className="grid grid-cols-3 gap-4 py-4">
//           {Object.keys(userData)
//             .filter((field) => field !== "status") // exclude status from default loop
//             .map((field) => (
//               <div key={field} className="grid grid-cols-1 items-center gap-4">
//                 <Label className="text-white">
//                   {field.replace(/([A-Z])/g, " $1")}
//                 </Label>
//                 <Input
//                   className="text-white"
//                   type="text"
//                   placeholder={field.replace(/([A-Z])/g, " $1")}
//                   name={field}
//                   value={userData[field]}
//                   onChange={handleChange}
//                 />
//               </div>
//             ))}

//           {/* Custom Status Dropdown */}
//           <div className="grid grid-cols-1 items-center gap-4">
//             <Label className="text-white">Status</Label>
//             <Select
//               value={userData.status}
//               onValueChange={(val) =>
//                 setUserData((prev) => ({ ...prev, status: val }))
//               }
//             >
//               <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
//                 <SelectValue placeholder="Select Status" />
//               </SelectTrigger>
//               <SelectContent className="border-zinc-700">
//                 <SelectGroup>
//                   {statusOptions.map((s) => (
//                     <SelectItem key={s.value} value={s.value}>
//                       {s.label}
//                     </SelectItem>
//                   ))}
//                 </SelectGroup>
//               </SelectContent>
//             </Select>
//           </div>
//         </div>

        
//         <DialogFooter className="mt-6 flex justify-between">
          
//           <Button
//             type="submit"
//             className="text-[16px] font-normal bg-zinc-800 text-white hover:bg-zinc-700"
//             onClick={handleUpdateUser}
//           >
//             Update User Details
//           </Button>
//         </DialogFooter>
//       </DialogContent>
//     </Dialog>
//   );
// }

